import { FilterOptionsState } from '@mui/material';

/**
 * Checks whether input is within the allowed Levenshtein distance of a same-length window inside label, starting at startIndex.
 */
const isWithinLevenshteinDistance = (input: string, label: string, startIndex: number, maxDistance: number): boolean => {
  const inputLength = input.length;

  // Compare input against a same-length window inside label, using two DP rows
  // instead of building the substring or allocating the full distance matrix.
  let previous = Array.from({ length: inputLength + 1 }, (_, i) => i);
  let current = new Array<number>(inputLength + 1);

  for (let i = 1; i <= inputLength; i++) {
    current[0] = i;
    let rowMinimum = current[0];

    for (let j = 1; j <= inputLength; j++) {
      const substitutionCost = input[i - 1] === label[startIndex + j - 1] ? 0 : 1;
      current[j] = Math.min(previous[j - 1] + substitutionCost, current[j - 1] + 1, previous[j] + 1);
      rowMinimum = Math.min(rowMinimum, current[j]);
    }

    // Once every value in a row is already above the allowed distance, this
    // window cannot become a valid fuzzy match.
    if (rowMinimum > maxDistance) return false;

    // Reuse the two row arrays across iterations to keep memory constant.
    const nextPrevious = current;
    current = previous;
    previous = nextPrevious;
  }

  return previous[inputLength] <= maxDistance;
};

/**
 * Creates a fuzzy filterOptions function compatible with MUI Autocomplete.
 *
 * Results are ordered: exact substring matches first, then fuzzy (typo-tolerant) matches.
 * The fuzzy tolerance scales with input length: floor(inputLength / 3), minimum 2.
 *
 * @param getLabel - How to extract a comparable string from an option.
 * Defaults to `String(option)`, so works out of the box for string options.
 *
 * @example
 * // String options (no argument needed)
 * filterOptions={createFuzzyFilterOptions()}
 *
 * @example
 * // Object options
 * filterOptions={createFuzzyFilterOptions((opt) => opt.siteName)}
 */
export function createFuzzyFilterOptions<T>(
  getLabel: (option: T) => string = (option) => String(option)
): (options: T[], state: FilterOptionsState<T>) => T[] {
  return (options, state) => {
    const input = state.inputValue.toLowerCase();
    if (!input) return options;

    const inputLength = input.length;
    const substringMatches: T[] = [];
    const fuzzyCandidates: { option: T; label: string }[] = [];

    for (const option of options) {
      const label = getLabel(option).toLowerCase();

      if (label.includes(input)) {
        substringMatches.push(option);
      } else if (label.length >= inputLength) {
        fuzzyCandidates.push({ option, label });
      }
    }

    const maxDistance = Math.max(2, Math.floor(inputLength / 3));
    const fuzzyMatches: T[] = [];

    for (const { option, label } of fuzzyCandidates) {
      for (let i = 0; i <= label.length - inputLength; i++) {
        if (isWithinLevenshteinDistance(input, label, i, maxDistance)) {
          fuzzyMatches.push(option);
          break;
        }
      }
    }

    return [...substringMatches, ...fuzzyMatches];
  };
}
